import * as Schema from "effect/Schema";

/**
 * `server.reportClientActivity`, which the vendored mobile client sends at launch
 * and whenever the app changes state.
 *
 * Upstream this is one half of a background-policy lease: the client reports
 * whether it is visible, focused and on battery, asks for background `scopes`
 * with a TTL, and the server answers through `server.getBackgroundPolicy` and
 * `subscribeBackgroundPolicy` — deciding how much work to keep doing on that
 * client's behalf.
 *
 * Forge has no such subsystem, and this does not invent one. The report is
 * accepted and deliberately dropped, which leaves Forge behaving exactly as it
 * does today: it has never varied its work by client presence. The two policy
 * methods stay unserved, so a client that wants an answer still degrades on that
 * feature rather than being told something untrue.
 *
 * What that buys is silence. Without it, every launch raised an error for a
 * report the client never needed an answer to.
 *
 * Only the fields worth reading are declared. An Effect struct drops what it does
 * not declare, so `scopes`, `ttlMs`, battery and network arrive and are
 * discarded; declaring them would imply Forge acts on them.
 */
export const ClientActivityReportInput = Schema.Struct({
  clientId: Schema.String,
  clientKind: Schema.String,
  visible: Schema.Boolean,
  focused: Schema.Boolean,
  recentlyInteracted: Schema.Boolean,
  appState: Schema.optionalKey(Schema.Literals(["active", "inactive", "background", "unknown"])),
  observedAt: Schema.String,
});
export type ClientActivityReportInput = typeof ClientActivityReportInput.Type;
